import React, { useEffect, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import axios from "axios";
import MotoristaAvaliacoes from "./components/MotoristaAvaliacoes";
import MotoristaPacotes from "./components/MotoristaPacotes";
import { t } from "../../lang/index";

type Avaliacao = {
    id: number;
    usuarioNome: string;
    usuarioFotoUrl?: string;
    nota: number;
    comentario: string;
    fotos?: number[];
    fotosPorPonto?: { fotoId: number; url: string; ponto?: string }[];
};

type Veiculo = {
    modelo?: string;
    capacidade?: number;
    fotoUrl?: string;
};

type Motorista = {
    id: number;
    nome: string;
    descricao?: string;
    idiomas?: string[];
    fotoId?: number;
    veiculo?: Veiculo;
    avaliacoes: Avaliacao[];
};

// ✅ Tipo estendido que inclui pacotes
type MotoristaComPacotes = Motorista & {
    pacotes?: any[];
};

interface MotoristaPerfilModalProps {
    open: boolean;
    onClose: () => void;
    motoristaId: number | null;
    baseUrl: string;
}

const MotoristaPerfilModal: React.FC<MotoristaPerfilModalProps> = ({
    open,
    onClose,
    motoristaId,
    baseUrl,
}) => {
    const [motorista, setMotorista] = useState<MotoristaComPacotes | null>(null);
    const [loading, setLoading] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSlides, setLightboxSlides] = useState<{ src: string }[]>([]);
    const [activeTab, setActiveTab] = useState<"avaliacoes" | "pacotes">(
        "avaliacoes"
    );

    // 🔹 Carrega os dados do motorista sempre que o modal abrir
    useEffect(() => {
        const fetchMotorista = async () => {
            if (!motoristaId) return;

            setLoading(true);
            try {
                // 1️⃣ Dados básicos do motorista
                const motoristaRes = await axios.get(`${baseUrl}/motorista/${motoristaId}`);
                const motoristaData: Motorista = motoristaRes.data;

                // 2️⃣ Avaliações do motorista
                const avaliacoesRes = await axios.get<Avaliacao[]>(
                    `${baseUrl}/avaliacoes/motorista/${motoristaId}`
                );

                // 3️⃣ Pacotes do motorista
                const pacotesRes = await axios.get(`${baseUrl}/pacotes/motorista/${motoristaId}`);

                // 4️⃣ Combina tudo
                setMotorista({
                    ...motoristaData,
                    avaliacoes: avaliacoesRes.data,
                    pacotes: pacotesRes.data,
                });
            } catch (err) {
                console.error("Erro ao carregar dados do motorista:", err);
            } finally {
                setLoading(false);
            }
        };

        if (open) fetchMotorista();
    }, [open, motoristaId, baseUrl]);

    const openLightbox = (slides: { src: string }[]) => {
        setLightboxSlides(slides);
        setLightboxOpen(true);
    };

    return (
        <>
            {/* Modal */}
            <div className={`modal fade ${open ? "show d-block" : ""}`} tabIndex={-1} aria-hidden={!open}>
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{loading ? t("home.driverModal.loading") : motorista?.nome}</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            {/* Info do motorista */}
                            {motorista && (
                                <div className="d-flex mb-3 align-items-center gap-3">
                                    <img
                                        src={
                                            motorista.fotoId
                                                ? `${baseUrl}/uploads/${motorista.fotoId}`
                                                : motorista.veiculo?.fotoUrl
                                                    ? `${baseUrl}${motorista.veiculo.fotoUrl}`
                                                    : "assets/img/users/default-user.jpg"
                                        }
                                        alt={motorista.nome}
                                        className="avatar avatar-xl rounded-circle"
                                    />
                                    <div>
                                        {motorista.descricao && (
                                            <p className="mb-1"><strong>{t("home.driverModal.description")}:</strong> {motorista.descricao}</p>
                                        )}
                                        <p className="mb-1"><strong>{t("home.driverModal.languages")}:</strong> {motorista.idiomas?.join(", ") || "N/A"}</p>
                                        <p className="mb-0">
                                            <strong>{t("home.driverModal.vehicle")}:</strong> {motorista.veiculo?.modelo || "N/A"} ({motorista.veiculo?.capacidade || "N/A"} seats)
                                        </p>
                                        <p className="mb-0">
                                            <strong>{t("home.driverModal.averageRating")}:</strong>{" "}
                                            {motorista.avaliacoes.length > 0 ? (
                                                <span className="badge badge-warning badge-xs text-gray-9 fs-10 fw-medium me-2 p-1">
                                                    <i className="isax isax-star" /> {(
                                                        motorista.avaliacoes.reduce((acc, a) => acc + a.nota, 0) / motorista.avaliacoes.length
                                                    ).toFixed(1)}
                                                </span>
                                            ) : (
                                                t("home.driverModal.noReviews")
                                            )}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Tabs */}
                            <ul className="nav mb-3" style={{ borderBottom: "1px solid #dee2e6" }}>
                                <li className="nav-item me-2">
                                    <button
                                        className={`nav-link ${activeTab === "avaliacoes" ? "active" : ""}`}
                                        onClick={() => setActiveTab("avaliacoes")}
                                        style={{
                                            borderRadius: "0.5rem",
                                            transition: "background-color 0.2s",
                                            color: activeTab === "avaliacoes" ? "#ed1c24" : "#495057",
                                            backgroundColor: activeTab === "avaliacoes" ? "rgba(237, 28, 36, 0.1)" : "transparent",
                                        }}
                                    >
                                        {t("home.driverModal.reviews")}
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === "pacotes" ? "active" : ""}`}
                                        onClick={() => setActiveTab("pacotes")}
                                        style={{
                                            borderRadius: "0.5rem",
                                            transition: "background-color 0.2s",
                                            color: activeTab === "pacotes" ? "#ed1c24" : "#495057",
                                            backgroundColor: activeTab === "pacotes" ? "rgba(237, 28, 36, 0.1)" : "transparent",
                                        }}
                                    >
                                        {t("home.driverModal.packages")}
                                    </button>
                                </li>
                            </ul>


                            {/* Conteúdo da tab */}
                            {activeTab === "avaliacoes" && (
                                <MotoristaAvaliacoes
                                    avaliacoes={motorista?.avaliacoes || []}
                                    baseUrl={baseUrl}
                                    openLightbox={openLightbox}
                                />
                            )}

                            {activeTab === "pacotes" && (
                                <MotoristaPacotes
                                    pacotes={motorista?.pacotes || []}
                                    baseUrl={baseUrl}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Backdrop escuro */}
            {open && <div className="modal-backdrop fade show"></div>}

            {/* Lightbox */}
            <Lightbox open={lightboxOpen} close={() => setLightboxOpen(false)} slides={lightboxSlides} />
        </>
    );
};

export default MotoristaPerfilModal;